import type { IQuery, IQueryHandler, IHttpClient } from '@/application/core';
import type {
  RegistrationSession,
  RegistrationSessionsResponse,
  RegistrationSessionsParams,
} from './registration-sessions.types';

// Re-export types for consumers that import directly from this file
export type { RegistrationSession, RetryAttempt, RegistrationSessionsResponse, RegistrationSessionsParams } from './registration-sessions.types';

// ============================================================================
// Query
// ============================================================================

export class GetRegistrationSessionsQuery implements IQuery<RegistrationSessionsResponse> {
  readonly type = 'GetRegistrationSessionsQuery' as const;
  constructor(public readonly params: RegistrationSessionsParams = {}) {}
}

// ============================================================================
// Handler
// ============================================================================

export class GetRegistrationSessionsQueryHandler
  implements IQueryHandler<GetRegistrationSessionsQuery, RegistrationSessionsResponse>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(query: GetRegistrationSessionsQuery): Promise<RegistrationSessionsResponse> {
    const params = new URLSearchParams();

    if (query.params.page) params.append('page', String(query.params.page));
    if (query.params.pageSize) params.append('pageSize', String(query.params.pageSize));
    if (query.params.fromDate) params.append('fromDate', query.params.fromDate);
    if (query.params.toDate) params.append('toDate', query.params.toDate);
    if (query.params.searchEmail) params.append('searchEmail', query.params.searchEmail);
    if (query.params.status) params.append('status', query.params.status);
    if (query.params.search) params.append('search', query.params.search);

    const url = `/api/sales/registration-sessions?${params.toString()}`;
    const response = await this.httpClient.get<RegistrationSessionsResponse>(url);

    // When the auth cookie has expired the server redirects the XHR to an HTML page, so the
    // resolved body has no `sessions` array. Surface a clear message instead of a cryptic
    // "Cannot read properties of undefined (reading 'map')" TypeError.
    if (!response || !Array.isArray(response.sessions)) {
      throw new Error('Could not load registration sessions. Your session may have expired — please refresh the page and sign in again.');
    }

    // Transform snake_case to camelCase
    return {
      sessions: response.sessions.map(this.transformSession),
      totalCount: response.totalCount ?? 0,
      page: response.page,
      pageSize: response.pageSize ?? 50,
    };
  }

  private transformSession(session: any): RegistrationSession {
    return {
      sessionId: session.sessionId ?? session.session_id,
      sessionType: session.sessionType ?? session.session_type,
      status: session.status,
      contactEmail: session.contactEmail ?? session.contact_email ?? '',
      contactName: session.contactName ?? session.contact_name,
      companyName: session.companyName ?? session.company_name,
      plan: session.plan,
      networkId: session.networkId ?? session.network_id,
      errorMessage: session.errorMessage ?? session.error_message,
      errorCheckpoint: session.errorCheckpoint ?? session.error_checkpoint,
      retryCount: session.retryCount ?? session.retry_count ?? 0,
      lastRetryAt: session.lastRetryAt ?? session.last_retry_at,
      createdAt: session.createdAt ?? session.created_at,
      updatedAt: session.updatedAt ?? session.updated_at,
      retryHistory: (session.retryHistory ?? session.retry_history ?? []).map(
        (attempt: any) => ({
          attemptedAt: attempt.attemptedAt ?? attempt.attempted_at,
          attemptedBy: attempt.attemptedBy ?? attempt.attempted_by,
          notes: attempt.notes,
          previousError: attempt.previousError ?? attempt.previous_error,
          previousCheckpoint: attempt.previousCheckpoint ?? attempt.previous_checkpoint,
          outcome: attempt.outcome,
          newError: attempt.newError ?? attempt.new_error,
          newCheckpoint: attempt.newCheckpoint ?? attempt.new_checkpoint,
        })
      ),
    };
  }
}
